import React, { useState } from "react";
import {
  Alert,
  Button,
  Modal,
  Result,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from "antd";
import fileDownload from "js-file-download";
import PropTypes from "prop-types";

import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  InboxOutlined,
} from "@ant-design/icons";

import {
  createBulkRevenueShare,
  validateBulkRevenueShare,
} from "../../Pages/api";

const { Text, Title } = Typography;
const { Dragger } = Upload;

// CSV field names that match API expectations
const CSV_HEADERS = ["email", "phone", "host", "revenueShare"];

// Required headers
const REQUIRED_HEADERS = ["host", "revenueShare"];

// Either email or phone must be present
const IDENTIFIER_HEADERS = ["email", "phone"];

const parseCsvData = (csvText) => {
  const lines = csvText.split("\n").filter((line) => line.trim());

  if (lines.length < 2) {
    throw new Error(
      "CSV file must contain at least a header row and one data row"
    );
  }

  // Parse headers
  const headers = lines[0].split(",").map((h) => h.trim());

  // Validate required headers
  const missingHeaders = REQUIRED_HEADERS.filter((h) => !headers.includes(h));
  if (missingHeaders.length > 0) {
    throw new Error(`Missing required columns: ${missingHeaders.join(", ")}`);
  }

  // Check that at least one identifier column (email or phone) is present
  const hasIdentifier = IDENTIFIER_HEADERS.some((h) => headers.includes(h));
  if (!hasIdentifier) {
    throw new Error(
      `At least one identifier column is required: ${IDENTIFIER_HEADERS.join(
        " or "
      )}`
    );
  }

  const entries = [];

  for (let i = 1; i < lines.length; i += 1) {
    // Handle CSV values with potential commas in quoted strings
    const values = [];
    let currentValue = "";
    let insideQuotes = false;

    for (let j = 0; j < lines[i].length; j += 1) {
      const char = lines[i][j];
      if (char === '"') {
        insideQuotes = !insideQuotes;
      } else if (char === "," && !insideQuotes) {
        values.push(currentValue.trim());
        currentValue = "";
      } else {
        currentValue += char;
      }
    }
    values.push(currentValue.trim());

    const entry = {};
    headers.forEach((header, index) => {
      const value = values[index] || "";
      // Clean up any surrounding quotes
      const cleanedValue = value.replace(/^"|"$/g, "").trim();
      // Only add non-empty values to the entry
      if (cleanedValue !== "") {
        entry[header] = cleanedValue;
      }
    });

    // Convert revenueShare to number or null
    if (entry.revenueShare !== undefined) {
      const rsValue = entry.revenueShare.toLowerCase();
      if (rsValue === "null" || rsValue === "") {
        entry.revenueShare = null;
      } else {
        entry.revenueShare = Number(entry.revenueShare);
      }
    }

    entries.push(entry);
  }

  return entries;
};

const generateSampleCSV = () => {
  const sampleData = [
    {
      email: "user1@example.com",
      phone: "9876543210",
      host: "tagmango.com",
      revenueShare: 15,
    },
    {
      email: "user2@example.com",
      phone: "9123456789",
      host: "tagmango.com",
      revenueShare: "null",
    },
    {
      email: "user3@example.com",
      phone: "9876543211",
      host: "tagmango.com",
      revenueShare: 0,
    },
    {
      email: "user4@example.com",
      phone: "",
      host: "tagmango.com",
      revenueShare: 25.5,
    },
  ];

  const header = CSV_HEADERS.join(",");
  const rows = sampleData.map((entry) =>
    CSV_HEADERS.map((h) => {
      const value = entry[h];
      if (value === null || value === undefined) return "";
      return value;
    }).join(",")
  );

  return `${header}\n${rows.join("\n")}`;
};

const generateValidationResultCSV = (entries) => {
  const headers = [
    "status",
    "email",
    "phone",
    "host",
    "userId",
    "revenueShare",
    "errorMessage",
  ];
  const header = headers.join(",");

  const rows = entries.map((entry) => {
    const rowData = {
      status: entry.isValid ? "Valid" : "Invalid",
      email: entry.email || "",
      phone: entry.phone || "",
      host: entry.host || "",
      userId: entry.userId || "",
      revenueShare:
        entry.revenueShare !== null ? entry.revenueShare : "Use Default",
      errorMessage: entry.errorMessage || "",
    };

    return headers
      .map((h) => {
        const value = rowData[h];
        if (value === undefined || value === null) return "";
        // Escape values with commas or quotes
        const strValue = String(value);
        if (strValue.includes(",") || strValue.includes('"')) {
          return `"${strValue.replace(/"/g, '""')}"`;
        }
        return strValue;
      })
      .join(",");
  });

  return `${header}\n${rows.join("\n")}`;
};

const BulkRevenueShareModal = ({ visible, onClose, onSuccess }) => {
  const [step, setStep] = useState("upload"); // upload, validating, results, creating, success
  const [validatedEntries, setValidatedEntries] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const resetModal = () => {
    setStep("upload");
    setValidatedEntries([]);
    setError(null);
    setLoading(false);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const handleDownloadSample = () => {
    const csv = generateSampleCSV();
    fileDownload(csv, "bulk_revenue_share_sample.csv");
  };

  const handleFileUpload = async (file) => {
    setError(null);
    setLoading(true);
    setStep("validating");

    try {
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const csvText = e.target.result;
          const entries = parseCsvData(csvText);

          if (entries.length === 0) {
            throw new Error("No valid entries found in CSV");
          }

          if (entries.length > 500) {
            throw new Error("Maximum 500 entries allowed per upload");
          }

          // Call validation API
          const response = await validateBulkRevenueShare({ entries });

          if (response?.code === 200 && response?.result?.entries) {
            setValidatedEntries(response.result.entries);
            setStep("results");
          } else {
            throw new Error(response?.message || "Validation failed");
          }
        } catch (err) {
          setError(err.message || "Error processing CSV file");
          setStep("upload");
        } finally {
          setLoading(false);
        }
      };

      reader.onerror = () => {
        setError("Error reading CSV file");
        setStep("upload");
        setLoading(false);
      };

      reader.readAsText(file);
    } catch (err) {
      setError(err.message || "Error uploading file");
      setStep("upload");
      setLoading(false);
    }

    return false; // Prevent default upload
  };

  const handleDownloadValidationResults = () => {
    const csv = generateValidationResultCSV(validatedEntries);
    fileDownload(csv, "bulk_revenue_share_validation_results.csv");
  };

  const handleCreateBulkUpdates = async () => {
    setLoading(true);
    setStep("creating");

    try {
      // Filter only valid entries and format for create API
      const validEntries = validatedEntries
        .filter((entry) => entry.isValid)
        .map((entry) => ({
          userId: entry.userId,
          revenueShare: entry.revenueShare,
        }));

      const response = await createBulkRevenueShare({ entries: validEntries });

      if (response?.code === 201 || response?.success) {
        setStep("success");
        if (onSuccess) {
          onSuccess();
        }
      } else {
        throw new Error(response?.message || "Failed to update revenue shares");
      }
    } catch (err) {
      setError(err.message || "Error updating revenue shares");
      setStep("results");
    } finally {
      setLoading(false);
    }
  };

  const allEntriesValid = validatedEntries.every((entry) => entry.isValid);
  const validCount = validatedEntries.filter((entry) => entry.isValid).length;
  const invalidCount = validatedEntries.filter(
    (entry) => !entry.isValid
  ).length;

  const columns = [
    {
      title: "Status",
      key: "status",
      width: 100,
      fixed: "left",
      render: (_, record) => {
        const tag = (
          <Tag
            icon={
              record.isValid ? <CheckCircleOutlined /> : <CloseCircleOutlined />
            }
            color={record.isValid ? "success" : "error"}
            style={{ cursor: record.isValid ? "default" : "pointer" }}
          >
            {record.isValid ? "Valid" : "Invalid"}
          </Tag>
        );

        // Show tooltip with error message only for invalid entries
        if (!record.isValid && record.errorMessage) {
          return (
            <Tooltip title={record.errorMessage} color="red">
              {tag}
            </Tooltip>
          );
        }

        return tag;
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 200,
      ellipsis: true,
      render: (email) => email || <Text type="secondary">-</Text>,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      width: 130,
      render: (phone) => phone || <Text type="secondary">-</Text>,
    },
    {
      title: "Host",
      dataIndex: "host",
      key: "host",
      width: 150,
      ellipsis: true,
    },
    {
      title: "User ID",
      dataIndex: "userId",
      key: "userId",
      width: 200,
      ellipsis: true,
      render: (userId, record) => {
        if (record.isValid && userId) {
          return (
            <Tooltip title={userId}>
              <Text code style={{ fontSize: 11 }}>
                {userId.substring(0, 8)}...
              </Text>
            </Tooltip>
          );
        }
        return <Text type="secondary">-</Text>;
      },
    },
    {
      title: "Revenue Share",
      dataIndex: "revenueShare",
      key: "revenueShare",
      width: 150,
      render: (value) => {
        if (value === null) {
          return (
            <Tooltip title="Will use product's default revenue share percentage">
              <Tag color="blue" style={{ cursor: "help" }}>
                Use Default
              </Tag>
            </Tooltip>
          );
        }
        if (value === 0) {
          return (
            <Tooltip title="0% commission - no earnings on sales">
              <Text strong style={{ color: "#ff4d4f" }}>
                {value}%
              </Text>
            </Tooltip>
          );
        }
        return (
          <Tooltip title="Custom revenue share percentage">
            <Text strong>{value}%</Text>
          </Tooltip>
        );
      },
    },
    {
      title: "Error",
      dataIndex: "errorMessage",
      key: "errorMessage",
      width: 300,
      ellipsis: true,
      render: (msg) => (msg ? <Text type="danger">{msg}</Text> : "-"),
    },
  ];

  const renderUploadStep = () => (
    <div style={{ padding: "20px 0" }}>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div>
          <Title level={5} style={{ marginBottom: 8 }}>
            Upload CSV File
          </Title>
          <Text type="secondary">
            Upload a CSV file with revenue share entries. Required columns:{" "}
            <Text strong>email/phone (at least one), host, revenueShare</Text>
          </Text>
          <div style={{ marginTop: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              💡 Use <Text code>null</Text> in the revenueShare column to reset
              partners to product default, or enter a custom percentage (0-100).
            </Text>
          </div>
        </div>

        <Button
          type="link"
          icon={<DownloadOutlined />}
          onClick={handleDownloadSample}
          style={{ padding: 0 }}
        >
          Download Sample CSV
        </Button>

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            closable
            onClose={() => setError(null)}
          />
        )}

        <Dragger
          name="file"
          accept=".csv"
          multiple={false}
          beforeUpload={handleFileUpload}
          showUploadList={false}
          disabled={loading}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Click or drag CSV file to this area to upload
          </p>
          <p className="ant-upload-hint">
            Only CSV files are supported. Maximum 500 entries per upload. The
            file will be validated before updates are applied.
          </p>
        </Dragger>

        <Alert
          message="Important Notes"
          description={
            <div>
              <ul style={{ margin: 0, paddingLeft: 20, marginBottom: 12 }}>
                <li>
                  <Text strong>email/phone:</Text> At least one is required.
                  Recommended to provide both for accurate partner
                  identification.
                </li>
                <li>
                  <Text strong>host:</Text> Partner&apos;s host domain (e.g.,
                  tagmango.com). Required field.
                </li>
                <li>
                  <Text strong>revenueShare:</Text> Must be provided for each
                  entry
                </li>
                <li>
                  <Text strong>Validation Response:</Text> After upload,
                  you&apos;ll see partner identification (email/phone/host),
                  User ID, and validation status
                </li>
                <li>
                  <Text strong>Limits:</Text> Maximum 500 entries per upload
                </li>
              </ul>
              <div
                style={{
                  background: "#f0f5ff",
                  padding: "12px",
                  borderRadius: "4px",
                  marginTop: 12,
                }}
              >
                <Text strong style={{ display: "block", marginBottom: 8 }}>
                  Understanding Revenue Share Values:
                </Text>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li>
                    <Text strong>Custom Percentage:</Text> Enter any number ≥ 0
                    (e.g., 0, 5, 10.5, 15, 25, 100)
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      This will override the product&apos;s default revenue
                      share for this specific partner
                    </Text>
                  </li>
                  <li style={{ marginTop: 8 }}>
                    <Text strong>
                      <Text code>null</Text> (Reset to Default):
                    </Text>{" "}
                    Use the word &quot;null&quot; without quotes
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      This removes any custom revenue share and makes the
                      partner use the product&apos;s default revenue share
                      percentage. If a partner currently has 15% custom share
                      and you set it to null, they&apos;ll start using whatever
                      the product default is (e.g., 10%)
                    </Text>
                  </li>
                  <li style={{ marginTop: 8 }}>
                    <Text strong>Zero (0%):</Text> Valid value for 0% revenue
                    share
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Partner will earn 0% commission on sales
                    </Text>
                  </li>
                </ul>
              </div>
            </div>
          }
          type="info"
          showIcon
        />
      </Space>
    </div>
  );

  const renderValidatingStep = () => (
    <div
      style={{
        padding: "60px 0",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Spin size="large" />
      <Text style={{ marginTop: 16 }}>Validating entries...</Text>
    </div>
  );

  const renderResultsStep = () => (
    <div style={{ padding: "10px 0" }}>
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Space>
            <Tag color="success" style={{ fontSize: 14, padding: "4px 12px" }}>
              Valid: {validCount}
            </Tag>
            <Tag color="error" style={{ fontSize: 14, padding: "4px 12px" }}>
              Invalid: {invalidCount}
            </Tag>
          </Space>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleDownloadValidationResults}
          >
            Download Results CSV
          </Button>
        </div>

        {!allEntriesValid && (
          <Alert
            message="Some entries have errors"
            description={
              <div>
                <p style={{ margin: 0, marginBottom: 8 }}>
                  {invalidCount} out of {validatedEntries.length} entries have
                  validation errors. Please review the errors below and fix them
                  in your CSV file.
                </p>
                <p style={{ margin: 0 }}>
                  <strong>Note:</strong> You can download the validation results
                  to see detailed error messages.
                </p>
              </div>
            }
            type="error"
            showIcon
          />
        )}

        {allEntriesValid && (
          <Alert
            message="All entries are valid!"
            description={
              <div>
                <p style={{ margin: 0, marginBottom: 8 }}>
                  All {validCount} entries have been validated successfully. The
                  table below shows the revenue share values to be applied for
                  each partner.
                </p>
                <p style={{ margin: 0 }}>
                  <strong>Review the changes carefully</strong> before clicking
                  &quot;Update Revenue Shares&quot; button.
                </p>
              </div>
            }
            type="success"
            showIcon
          />
        )}

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            closable
            onClose={() => setError(null)}
          />
        )}

        <Table
          columns={columns}
          dataSource={validatedEntries.map((entry, index) => ({
            ...entry,
            key: index,
          }))}
          size="small"
          scroll={{ x: 1200 }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} entries`,
          }}
          rowClassName={(record) =>
            record.isValid ? "valid-row" : "invalid-row"
          }
        />
      </Space>
    </div>
  );

  const renderCreatingStep = () => (
    <div
      style={{
        padding: "60px 0",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Spin size="large" />
      <Text style={{ marginTop: 16 }}>Updating revenue shares...</Text>
      <Text type="secondary" style={{ marginTop: 8 }}>
        Please do not close this window
      </Text>
    </div>
  );

  const renderSuccessStep = () => (
    <Result
      status="success"
      title="Revenue Shares Updated Successfully!"
      subTitle={`${validCount} revenue share(s) have been updated successfully.`}
      extra={[
        <Button key="close" type="primary" onClick={handleClose}>
          Close
        </Button>,
      ]}
    />
  );

  const renderContent = () => {
    switch (step) {
      case "upload":
        return renderUploadStep();
      case "validating":
        return renderValidatingStep();
      case "results":
        return renderResultsStep();
      case "creating":
        return renderCreatingStep();
      case "success":
        return renderSuccessStep();
      default:
        return renderUploadStep();
    }
  };

  const renderFooter = () => {
    if (step === "upload") {
      return [
        <Button key="cancel" onClick={handleClose}>
          Cancel
        </Button>,
      ];
    }

    if (step === "results") {
      return [
        <Button key="back" onClick={() => setStep("upload")}>
          Upload New File
        </Button>,
        <Button
          key="submit"
          type="primary"
          disabled={!allEntriesValid || loading}
          onClick={handleCreateBulkUpdates}
          loading={loading}
        >
          Update Revenue Shares ({validCount})
        </Button>,
      ];
    }

    if (step === "success") {
      return null; // Footer handled in success result
    }

    return null;
  };

  return (
    <Modal
      title="Bulk Revenue Share Management"
      open={visible}
      onCancel={handleClose}
      width={step === "results" ? 1200 : 700}
      footer={renderFooter()}
      destroyOnClose
      maskClosable={step === "upload"}
    >
      {renderContent()}
    </Modal>
  );
};

BulkRevenueShareModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

BulkRevenueShareModal.defaultProps = {
  onSuccess: () => {},
};

export default BulkRevenueShareModal;
